import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SquaresFour, ForkKnife, FloppyDisk, Monitor, CurrencyDollar, Lock, Database, Download, Upload, Sparkle, Broom, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { downloadCsv } from "@/utils/downloadCsv";

/**
 * PosSettingsContent — the POS Settings form, rendered inside
 * Settings → Equipment → POS as an embedded tab. There is no longer a
 * standalone route or Sidebar entry for POS Settings.
 */
export const PosSettingsContent = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    pos_startup_with_tables: true,
    tip_preset_1: 15,
    tip_preset_2: 18,
    tip_preset_3: 20,
    customer_display_enabled: false,
    auto_logout_minutes: 0,
    external_terminal_type: "",
    external_terminal_id: "",
    external_terminal_url: "",
    cash_discount_enabled: false,
    cash_discount_percentage: 3.5,
    cash_discount_label: "Non-Cash Adjustment",
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/store-settings`);
      setSettings({
        pos_startup_with_tables: res.data?.pos_startup_with_tables ?? true,
        tip_preset_1: res.data?.tip_preset_1 ?? 15,
        tip_preset_2: res.data?.tip_preset_2 ?? 18,
        tip_preset_3: res.data?.tip_preset_3 ?? 20,
        customer_display_enabled: !!res.data?.customer_display_enabled,
        auto_logout_minutes: res.data?.auto_logout_minutes ?? 0,
        external_terminal_type: res.data?.external_terminal_type ?? "",
        external_terminal_id: res.data?.external_terminal_id ?? "",
        external_terminal_url: res.data?.external_terminal_url ?? "",
        cash_discount_enabled: !!res.data?.cash_discount_enabled,
        cash_discount_percentage: res.data?.cash_discount_percentage ?? 3.5,
        cash_discount_label: res.data?.cash_discount_label ?? "Non-Cash Adjustment",
      });
    } catch (err) {
      toast.error("Failed to load POS settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/store-settings`, {
        pos_startup_with_tables: settings.pos_startup_with_tables,
        tip_preset_1: parseFloat(settings.tip_preset_1) || 0,
        tip_preset_2: parseFloat(settings.tip_preset_2) || 0,
        tip_preset_3: parseFloat(settings.tip_preset_3) || 0,
        customer_display_enabled: settings.customer_display_enabled,
        auto_logout_minutes: Math.max(0, parseInt(settings.auto_logout_minutes, 10) || 0),
        external_terminal_type: (settings.external_terminal_type || "").trim(),
        external_terminal_id: (settings.external_terminal_id || "").trim(),
        external_terminal_url: (settings.external_terminal_url || "").trim(),
        cash_discount_enabled: !!settings.cash_discount_enabled,
        cash_discount_percentage: Math.max(0, Math.min(100, parseFloat(settings.cash_discount_percentage) || 0)),
        cash_discount_label: (settings.cash_discount_label || "").trim() || "Non-Cash Adjustment",
      });
      toast.success("POS settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const set = (key, value) => setSettings(s => ({ ...s, [key]: value }));

  const loader = (
    <div className="flex items-center justify-center py-20">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="pos-settings-embedded">
        {loading ? loader : (
          <>
            {/* Startup card */}
            <Card data-testid="startup-screen-card">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Startup Screen</CardTitle>
                <CardDescription>Choose what staff see when they open the POS.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-card">
                  <div className="space-y-1">
                    <Label htmlFor="startup-toggle" className="text-base font-medium">Start with Table View</Label>
                    <p className="text-sm text-muted-foreground">
                      {settings.pos_startup_with_tables
                        ? "POS opens on the Tables (floor plan) screen."
                        : "POS opens directly on the Orders / Menu screen (bar / quick-order mode)."}
                    </p>
                  </div>
                  <Switch
                    id="startup-toggle"
                    checked={settings.pos_startup_with_tables}
                    onCheckedChange={(v) => set("pos_startup_with_tables", v)}
                    data-testid="startup-with-tables-toggle"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => set("pos_startup_with_tables", true)}
                    className={`text-left p-4 rounded-lg border-2 transition-colors ${settings.pos_startup_with_tables ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/40"}`}
                    data-testid="startup-tables-option"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <SquaresFour size={18} className="text-primary" weight="fill" />
                      <span className="font-medium">Tables First</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Floor plan opens by default. Best for full-service dining.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => set("pos_startup_with_tables", false)}
                    className={`text-left p-4 rounded-lg border-2 transition-colors ${!settings.pos_startup_with_tables ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/40"}`}
                    data-testid="startup-orders-option"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <ForkKnife size={18} className="text-primary" weight="fill" />
                      <span className="font-medium">Orders First</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Skip table view — open straight to Orders / Menu. Best for bar or quick-service.</p>
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Tip presets */}
            <Card data-testid="tip-presets-card">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <CurrencyDollar size={20} weight="fill" /> Tip Presets
                </CardTitle>
                <CardDescription>
                  Three percentage shortcuts shown on the tip step of the pay wizard. The guest can also pick a custom amount or no tip.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3].map((n) => (
                    <div key={n} className="space-y-1">
                      <Label>Preset {n} (%)</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        className="font-mono text-lg h-12"
                        value={settings[`tip_preset_${n}`]}
                        onChange={(e) => set(`tip_preset_${n}`, e.target.value)}
                        data-testid={`tip-preset-${n}-input`}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Cash Discount / Dual Pricing — the restaurant either eats
                the card fee itself (toggle off) or passes it to non-cash
                customers as a surcharge on the total. Legal in most US
                states as a "Dual Pricing" or "Cash Discount Program". */}
            <Card data-testid="cash-discount-card">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <CurrencyDollar size={20} weight="fill" /> Cash Discount / Dual Pricing
                </CardTitle>
                <CardDescription>
                  Decide who pays the card processing fee. Off = the restaurant absorbs it.
                  On = non-cash guests see a small surcharge line on their total (cash and
                  gift-card guests never see it). Configure the percentage and receipt label below.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-card">
                  <div className="space-y-1">
                    <Label htmlFor="cash-discount-toggle" className="text-base font-medium">
                      Pass card fees to the customer
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      When on, the pay wizard adds a &ldquo;{settings.cash_discount_label || "Non-Cash Adjustment"}&rdquo;
                      line to card, pinpad, and other non-cash tenders.
                    </p>
                  </div>
                  <Switch
                    id="cash-discount-toggle"
                    checked={settings.cash_discount_enabled}
                    onCheckedChange={(v) => set("cash_discount_enabled", v)}
                    data-testid="cash-discount-toggle"
                  />
                </div>
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 transition-opacity ${settings.cash_discount_enabled ? "" : "opacity-60"}`}>
                  <div className="space-y-1">
                    <Label>Surcharge percentage</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={settings.cash_discount_percentage}
                        onChange={(e) => set("cash_discount_percentage", e.target.value)}
                        disabled={!settings.cash_discount_enabled}
                        data-testid="cash-discount-pct-input"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Typically 3.0-4.0%. Check your state/processor rules on maximum allowed surcharge.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label>Receipt label</Label>
                    <Input
                      value={settings.cash_discount_label}
                      onChange={(e) => set("cash_discount_label", e.target.value)}
                      disabled={!settings.cash_discount_enabled}
                      placeholder="Non-Cash Adjustment"
                      data-testid="cash-discount-label-input"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Shown on the pay screen, receipt, and refund records for this line item.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer display */}
            <Card data-testid="customer-display-card">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Monitor size={20} weight="fill" /> Customer Display
                </CardTitle>
                <CardDescription>
                  When enabled, pressing Pay opens a separate window for the guest to choose tip and loyalty. The cashier&apos;s screen stays in a &ldquo;waiting&rdquo; state until the guest finishes.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-card">
                  <div className="space-y-1">
                    <Label htmlFor="cfd-toggle" className="text-base font-medium">Use customer-facing window</Label>
                    <p className="text-sm text-muted-foreground">
                      Drag the window to your secondary monitor once, click <em>Pin to this display</em>, and the browser will remember its position from then on.
                    </p>
                  </div>
                  <Switch
                    id="cfd-toggle"
                    checked={settings.customer_display_enabled}
                    onCheckedChange={async (v) => {
                      set("customer_display_enabled", v);
                      // Proactively ask for the Window Management permission
                      // the instant the cashier enables CFD. The toggle click
                      // is a real user gesture, so the prompt is allowed.
                      if (!v || typeof window.getScreenDetails !== "function") return;
                      // Skip the prompt only if we KNOW it's already decided.
                      // If the permissions query throws (older browser that
                      // doesn't recognise the name), fall through and ask anyway.
                      let alreadyDecided = false;
                      try {
                        if (navigator.permissions?.query) {
                          const status = await navigator.permissions.query({ name: "window-management" });
                          alreadyDecided = status.state === "granted" || status.state === "denied";
                        }
                      } catch { /* unknown name — proceed to ask */ }
                      if (!alreadyDecided) {
                        try { await window.getScreenDetails(); } catch { /* user dismissed */ }
                      }
                      window.dispatchEvent(new Event("cfd-permission-refresh"));
                    }}
                    data-testid="customer-display-toggle"
                  />
                </div>
                {settings.customer_display_enabled && <MultiScreenAccessBanner />}
              </CardContent>
            </Card>

            {/* External pinpad / EMV terminal — model-agnostic dispatch.
                Providers here are the *integration types*, not physical
                models. Any Authorize.net-certified pinpad (Ingenico Lane,
                Verifone P400, PAX A80, MagTek DynaPro, ID Tech Vendi, …) is
                supported by the same `authnet_semi_integrated` provider —
                you just pick the device in Authorize.net's portal and paste
                the device ID below. Change hardware later? Update the ID,
                no code changes needed. */}
            <Card data-testid="external-terminal-card">
              <CardHeader>
                <CardTitle>External Terminal (pinpad)</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Send card charges to a countertop pinpad instead of the on-screen card form.
                  Works with any Authorize.net-certified device — Ingenico, Verifone, PAX, MagTek, ID Tech, etc.
                  The specific model isn&apos;t configured here; it&apos;s registered inside your Authorize.net
                  merchant portal and referenced by its Device ID.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <Label>Provider</Label>
                  <Select
                    value={settings.external_terminal_type || "none"}
                    onValueChange={(v) => set("external_terminal_type", v === "none" ? "" : v)}
                  >
                    <SelectTrigger data-testid="external-terminal-type-select">
                      <SelectValue placeholder="Choose a pinpad provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" data-testid="ext-term-opt-none">
                        None — manual entry / magswipe only
                      </SelectItem>
                      <SelectItem value="authnet_semi_integrated" data-testid="ext-term-opt-authnet">
                        Authorize.net Semi-Integrated (any certified device)
                      </SelectItem>
                      <SelectItem value="generic_http" data-testid="ext-term-opt-http">
                        Generic HTTP pinpad (LAN device with its own API)
                      </SelectItem>
                      <SelectItem value="test_mode" data-testid="ext-term-opt-test">
                        Test Mode (fake approved — for training / release QA)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {settings.external_terminal_type === "authnet_semi_integrated" && (
                  <div className="space-y-1">
                    <Label>Authorize.net Device ID</Label>
                    <Input
                      placeholder="From Merchant Interface → Tools → Accept Suite → Devices"
                      value={settings.external_terminal_id}
                      onChange={(e) => set("external_terminal_id", e.target.value)}
                      data-testid="external-terminal-id"
                    />
                    <p className="text-xs text-muted-foreground pt-1">
                      Register your pinpad model in the Authorize.net Merchant Interface, then paste the
                      Device ID it assigns. Swapping hardware later? Just update this ID.
                    </p>
                  </div>
                )}

                {settings.external_terminal_type === "generic_http" && (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label>LAN URL</Label>
                      <Input
                        placeholder="http://192.168.1.50:8080"
                        value={settings.external_terminal_url}
                        onChange={(e) => set("external_terminal_url", e.target.value)}
                        data-testid="external-terminal-url"
                      />
                      <p className="text-xs text-muted-foreground pt-1">
                        POS will POST <code>{`{amount, order_id}`}</code> to
                        <code> {"{URL}"}/txn</code> and expect JSON back:
                        <code> {`{status:"approved"|"declined", transaction_id, auth_code?, message?}`}</code>.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label>Device auth token (optional)</Label>
                      <Input
                        placeholder="If your pinpad requires an auth token"
                        value={settings.external_terminal_id}
                        onChange={(e) => set("external_terminal_id", e.target.value)}
                        data-testid="external-terminal-id"
                      />
                    </div>
                  </div>
                )}

                {settings.external_terminal_type === "test_mode" && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                    <strong>Test Mode active</strong> — pinpad payments will return a fake
                    approved transaction after a 2 second delay. No real gateway is contacted.
                    Switch this back before opening the doors.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Auto-logout for inactivity */}
            <Card data-testid="auto-logout-card">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Lock size={20} weight="fill" /> Inactivity Auto-Logout
                </CardTitle>
                <CardDescription>
                  Automatically sign out the current user after this many minutes of no
                  mouse, keyboard or touch activity. A 30-second warning toast shows first.
                  Set to <span className="font-mono">0</span> to disable. Auto-logout does
                  not clock the user out — staff must hit the <em>Clock Out</em> button on
                  the login screen when they actually end their shift.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                  <div className="flex-1 max-w-xs space-y-1">
                    <Label>Idle timeout (minutes)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={240}
                      step={1}
                      className="font-mono text-lg h-12"
                      value={settings.auto_logout_minutes}
                      onChange={(e) => set("auto_logout_minutes", e.target.value)}
                      data-testid="auto-logout-input"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {[0, 5, 10, 15, 30].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => set("auto_logout_minutes", m)}
                        className={`px-3 py-2 text-xs rounded-md border transition-colors ${
                          Number(settings.auto_logout_minutes) === m
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border text-muted-foreground hover:border-muted-foreground/40"
                        }`}
                        data-testid={`auto-logout-preset-${m}`}
                      >
                        {m === 0 ? "Off" : `${m} min`}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer & Loyalty database — Import / Export CSV */}
            <CustomerLoyaltyDataCard />

            {/* Demo / Training Mode — hidden at the bottom */}
            <DemoModeCard />

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-pos-settings-btn">
                <FloppyDisk size={18} weight="bold" />
                {saving ? "Saving…" : "Save Settings"}
              </Button>
            </div>
          </>
        )}
    </div>
  );
};

// ============================================================================
//   Customer & Loyalty Database — Import + Export CSV
// ============================================================================
const CustomerLoyaltyDataCard = () => {
  const [busy, setBusy] = useState(null); // "cust" | "loyalty" | null

  const handleImport = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    const form = new FormData();
    form.append("file", file);
    const url = kind === "cust"
      ? `${API}/customers/import`
      : `${API}/loyalty/customers/import`;
    try {
      const res = await axios.post(url, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data;
      const parts = [];
      if (d.created) parts.push(`${d.created} created`);
      if (d.updated) parts.push(`${d.updated} updated`);
      if (d.skipped) parts.push(`${d.skipped} skipped`);
      toast.success(parts.length ? parts.join(", ") : `No changes (${d.rows_seen} rows scanned)`);
      if (d.errors?.length) {
        toast.warning(`First errors: ${d.errors.slice(0, 3).join("; ")}`);
      }
    } catch (e) {
      toast.error("Import failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card data-testid="customer-loyalty-data-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Database size={20} weight="fill" /> Customer &amp; Loyalty Database
        </CardTitle>
        <CardDescription>
          Export your guest list and rewards programme for backup, payroll,
          or migration to another POS. Import the same files back to seed a
          new install. Matching is by email then phone — found rows update,
          missing rows insert.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <DataRow
          label="Customers"
          description="Name, contact, visit count, total spent, tags."
          onExport={() => downloadCsv(`${API}/customers/export.csv`)}
          onImport={(f) => handleImport("cust", f)}
          busy={busy === "cust"}
          testIdPrefix="cust"
        />
        <DataRow
          label="Loyalty Members"
          description="Tier, points balance, lifetime spend, enrollment date."
          onExport={() => downloadCsv(`${API}/loyalty/customers/export.csv`)}
          onImport={(f) => handleImport("loyalty", f)}
          busy={busy === "loyalty"}
          testIdPrefix="loyalty"
        />
      </CardContent>
    </Card>
  );
};

const DataRow = ({ label, description, onExport, onImport, busy, testIdPrefix }) => {
  const inputId = `${testIdPrefix}-csv-upload`;
  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-lg border border-border p-3 bg-secondary/20">
      <div className="min-w-0">
        <div className="font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          className="gap-1.5"
          onClick={onExport}
          disabled={busy}
          data-testid={`${testIdPrefix}-export-btn`}
        >
          <Download size={16} /> Export CSV
        </Button>
        <input
          id={inputId}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onImport(f);
            e.target.value = ""; // allow re-selecting the same file
          }}
          data-testid={`${testIdPrefix}-import-input`}
        />
        <Button
          variant="outline"
          className="gap-1.5"
          onClick={() => document.getElementById(inputId).click()}
          disabled={busy}
          data-testid={`${testIdPrefix}-import-btn`}
        >
          <Upload size={16} /> {busy ? "Importing…" : "Import CSV"}
        </Button>
      </div>
    </div>
  );
};

export default function PosSettingsPage() {
  // Legacy default export kept only because a few older imports still
  // point here; the standalone page has moved into Settings → Equipment.
  return <PosSettingsContent />;
}

// ============================================================================
//   Multi-Screen Access banner — proactively unlocks the Window Management
//   API so the CFD popup auto-finds the 2nd display on the very first
//   checkout, without the cashier having to drag-and-pin first.
//
//   Browser support note: Chromium-based (Chrome / Edge / Brave / Opera /
//   Arc) ≥ v100. Firefox and Safari don't ship the API — the banner hides
//   itself there so we don't show a button that can't possibly work.
// ============================================================================
const MultiScreenAccessBanner = () => {
  const [state, setState] = useState("checking"); // checking | unsupported | prompt | granted | denied
  const [screens, setScreens] = useState([]);

  const supported = typeof window !== "undefined" && typeof window.getScreenDetails === "function";

  const refresh = async () => {
    if (!supported) { setState("unsupported"); return; }
    try {
      if (navigator.permissions?.query) {
        const status = await navigator.permissions.query({ name: "window-management" });
        if (status.state === "granted") {
          setState("granted");
          try {
            const details = await window.getScreenDetails();
            setScreens(details.screens || []);
          } catch { setScreens([]); }
          return;
        }
        if (status.state === "denied") { setState("denied"); return; }
      }
      setState("prompt");
    } catch {
      setState("prompt");
    }
  };

  useEffect(() => { refresh(); }, []);

  // Re-check permission state whenever the toggle handler signals it has
  // tried to ask. Banner flips from "prompt" → "granted"/"denied" without
  // requiring a page reload.
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("cfd-permission-refresh", handler);
    return () => window.removeEventListener("cfd-permission-refresh", handler);
  }, []);

  const handleEnable = async () => {
    // getScreenDetails() from a click triggers the permission prompt in
    // Chromium-based browsers. We don't actually need the result here —
    // refresh() picks it up via the Permissions API.
    try {
      const details = await window.getScreenDetails();
      setScreens(details.screens || []);
      setState("granted");
      toast.success(`Multi-screen access enabled — found ${details.screens?.length || 0} display${(details.screens?.length || 0) === 1 ? "" : "s"}.`);
    } catch (e) {
      if (String(e?.message || e).toLowerCase().includes("denied")) {
        setState("denied");
        toast.error("Multi-screen access denied. Re-enable it from the browser's site settings.");
      } else {
        // Some Chromium builds reject without "denied" if the user dismissed
        // the prompt — treat as still-pending.
        setState("prompt");
      }
    }
  };

  if (state === "checking") return null;

  if (state === "unsupported") {
    return (
      <div className="flex items-start gap-3 p-3 rounded-lg border border-dashed bg-secondary/20 text-xs" data-testid="multiscreen-unsupported">
        <Monitor size={18} className="shrink-0 mt-0.5 text-muted-foreground" weight="duotone" />
        <div>
          <p className="font-medium text-sm mb-0.5">Multi-screen auto-detection not available</p>
          <p className="text-muted-foreground">
            This browser doesn&apos;t support the Window Management API (Firefox / Safari). The popup will still work — just drag it to the customer display once and click <em>Pin to this display</em>.
          </p>
        </div>
      </div>
    );
  }

  if (state === "granted") {
    return (
      <div className="flex items-start gap-3 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 text-xs" data-testid="multiscreen-granted">
        <CheckCircle size={18} className="shrink-0 mt-0.5 text-emerald-600" weight="fill" />
        <div className="flex-1">
          <p className="font-medium text-sm mb-0.5">Multi-screen access enabled</p>
          <p className="text-muted-foreground">
            {screens.length > 1
              ? `Detected ${screens.length} displays. The popup will open on the largest non-primary screen automatically.`
              : "Only one display detected. Connect your customer-facing display now and the next checkout will use it automatically."}
          </p>
        </div>
      </div>
    );
  }

  if (state === "denied") {
    return (
      <div className="flex items-start gap-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 text-xs" data-testid="multiscreen-denied">
        <WarningCircle size={18} className="shrink-0 mt-0.5 text-amber-600" weight="fill" />
        <div>
          <p className="font-medium text-sm mb-0.5">Multi-screen access blocked</p>
          <p className="text-muted-foreground">
            Click the lock / settings icon in the address bar, find <span className="font-mono">Window management</span>, and switch it to <em>Allow</em>. Then reload this page. The drag-and-pin workflow still works in the meantime.
          </p>
        </div>
      </div>
    );
  }

  // state === "prompt"
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5" data-testid="multiscreen-prompt">
      <Monitor size={18} className="shrink-0 mt-0.5 text-primary" weight="fill" />
      <div className="flex-1 space-y-1">
        <p className="font-medium text-sm">Enable multi-screen support (recommended)</p>
        <p className="text-xs text-muted-foreground">
          Lets the customer display popup find your secondary monitor on the very first checkout — no drag-and-pin needed. The browser will ask once; click <em>Allow</em>.
        </p>
      </div>
      <Button
        size="sm"
        className="gap-1.5 shrink-0"
        onClick={handleEnable}
        data-testid="multiscreen-enable-btn"
      >
        <Monitor size={14} weight="fill" />
        Enable
      </Button>
    </div>
  );
};

// ============================================================================
//   Demo / Training Mode — hidden under POS Settings (manager + admin)
//   Loads a scripted lunch-rush scenario for training and demos. Every doc
//   is tagged is_demo:true so "Clear Demo Data" wipes the whole scenario in
//   one shot, without touching real orders, reservations or loyalty members.
// ============================================================================
const DemoModeCard = () => {
  const [status, setStatus] = useState({ active: false, counts: {}, simulation: { running: false, interval_seconds: 45 }, loyalty_lookup_phone: null });
  const [busy, setBusy] = useState(null); // "seed" | "clear" | "sim" | null
  const [loaded, setLoaded] = useState(false);
  // Detect the isolated demo window (opened via /pos?demo=1). Inside the
  // demo window this card lets the trainer seed / clear / simulate as
  // usual; from the main POS it becomes a launcher that pops a fresh demo
  // window so real service is never touched.
  const isDemoWindow = typeof window !== "undefined"
    && window.__POS_DEMO_MODE__ === true;
  const openDemoWindow = () => {
    const url = "/pos?demo=1";
    const features = "noopener,noreferrer,width=1400,height=900,menubar=no,location=no";
    window.open(url, "pos-demo-window", features);
    toast.success("Demo window opened — real service is unaffected.");
  };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/demo/status`);
      setStatus((prev) => ({ ...prev, ...res.data }));
    } catch (_e) {
      // Silent — manager hasn't opened this section yet.
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (!isDemoWindow) { setLoaded(true); return; }
    fetchStatus();
    // Refresh while sim is running so the badge stays in sync.
    const t = setInterval(fetchStatus, 15000);
    return () => clearInterval(t);
     
  }, [isDemoWindow]);

  const handleSeed = async () => {
    if (status.active && !window.confirm(
      "Demo data is already loaded. Reseed will wipe the current demo scenario and start fresh. Continue?"
    )) return;
    setBusy("seed");
    try {
      const res = await axios.post(`${API}/demo/seed`);
      const c = res.data?.inserted || {};
      toast.success(
        `Lunch-rush demo loaded — ${c.orders || 0} tickets, ${c.reservations || 0} reservations, ${c.transactions || 0} partial pay, ${c.loyalty_customers || 0} loyalty member.`
      );
      if (res.data?.loyalty_lookup_phone) {
        toast.info(`Demo loyalty phone: ${res.data.loyalty_lookup_phone}`, { duration: 8000 });
      }
      await fetchStatus();
    } catch (e) {
      toast.error("Demo seed failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Wipe all demo orders, reservations, partial payments and the demo loyalty member?")) return;
    setBusy("clear");
    try {
      const res = await axios.post(`${API}/demo/clear`);
      const d = res.data?.deleted || {};
      const total = (d.orders || 0) + (d.reservations || 0) + (d.transactions || 0) + (d.loyalty_customers || 0);
      toast.success(`Cleared ${total} demo record${total === 1 ? "" : "s"} and restored ${d.tables_restored || 0} table${d.tables_restored === 1 ? "" : "s"}.`);
      await fetchStatus();
    } catch (e) {
      toast.error("Clear failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const handleSimToggle = async (next) => {
    setBusy("sim");
    try {
      const endpoint = next ? "/demo/simulate/start" : "/demo/simulate/stop";
      const res = await axios.post(`${API}${endpoint}`);
      toast.success(res.data?.message || (next ? "Simulation started" : "Simulation stopped"));
      await fetchStatus();
    } catch (e) {
      toast.error("Simulation toggle failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  if (!loaded) return null;

  const counts = status.counts || {};
  const sim = status.simulation || { running: false, interval_seconds: 45 };

  return (
    <Card data-testid="demo-mode-card" className="border-dashed">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Sparkle size={20} weight="fill" /> Demo / Training Mode
          {status.active && (
            <span
              className="ml-2 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full bg-primary/15 text-primary border border-primary/30"
              data-testid="demo-active-badge"
            >
              Demo Active
            </span>
          )}
        </CardTitle>
        <CardDescription>
          {isDemoWindow ? (
            <>Loads a scripted lunch rush inside this isolated demo window — four open tickets at different workflow stages, two upcoming reservations, one partial-pay scenario, and a Gold-tier loyalty walk-up. Everything here runs against a completely separate database so real orders, reports, and labor stay untouched.</>
          ) : (
            <>Opens an <strong>isolated demo window</strong> — a completely separate copy of the POS running on a private database. Perfect for training a new employee mid-rush without polluting real orders, reports, or the kitchen display. Close the window when done; nothing carries over.</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.active && (
          <div
            className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center text-xs"
            data-testid="demo-counts"
          >
            {[
              ["Tickets", counts.orders],
              ["Reservations", counts.reservations],
              ["Partial Pay", counts.transactions],
              ["Loyalty", counts.loyalty_customers],
              ["Tables", counts.tables_repurposed],
            ].map(([label, n]) => (
              <div key={label} className="rounded-md border border-border bg-secondary/30 py-2">
                <div className="font-mono text-base">{n ?? 0}</div>
                <div className="text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {isDemoWindow
              ? (status.active
                  ? "Demo data is loaded in this isolated window."
                  : "This is the isolated demo window. Load the lunch rush to begin.")
              : "Click Launch Demo to open a private training window. Real service continues untouched."}
          </p>
          <div className="flex gap-2">
            {!isDemoWindow ? (
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={openDemoWindow}
                data-testid="launch-demo-btn"
              >
                <Sparkle size={16} weight="fill" />
                Launch Demo
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="gap-1.5"
                  onClick={handleSeed}
                  disabled={busy !== null}
                  data-testid="demo-seed-btn"
                >
                  <Sparkle size={16} weight="fill" />
                  {busy === "seed" ? "Loading…" : status.active ? "Reload Demo" : "Load Lunch Rush"}
                </Button>
                {status.active && (
                  <Button
                    variant="outline"
                    className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                    onClick={handleClear}
                    disabled={busy !== null}
                    data-testid="demo-clear-btn"
                  >
                    <Broom size={16} weight="fill" />
                    {busy === "clear" ? "Clearing…" : "Clear Demo Data"}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {status.active && (
          <div className="flex items-start justify-between gap-4 p-3 rounded-lg border bg-secondary/20" data-testid="demo-simulation-row">
            <div className="space-y-0.5">
              <Label htmlFor="sim-toggle" className="text-sm font-medium">Live Simulation</Label>
              <p className="text-xs text-muted-foreground">
                When ON, the demo tickets advance one step every{" "}
                {sim.interval_seconds || 45}s
                {" "}(pending → in-progress → ready → served → paid → recycled).
                Great for training kitchen-display response without a real rush.
              </p>
            </div>
            <Switch
              id="sim-toggle"
              checked={!!sim.running}
              onCheckedChange={(v) => handleSimToggle(v)}
              disabled={busy !== null}
              data-testid="demo-simulation-toggle"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
